/* =================================================================
   CORPORATE GRIND - Scroll Watchdog v2.2.0 POST-LEGACY
   
   v2.2 CHANGE: Runs cleanup AFTER legacy scripts with MutationObserver
   
   This watchdog:
   - Uses MutationObserver to detect and clear style mutations
   - Runs multiple cleanup passes to catch late-running scripts
   - Uses document.scrollingElement as source of truth
   
   TEMPORARY MITIGATION
   P0-FRAMEWORK: v1.0
   REMOVAL-CRITERIA: CDN serves clean v3.3 animation snippet until CDN serves new code
   
   VERSION: 2.2.0
   DATE: 2026-03-12
   SEVERITY: P0
   ================================================================= */
(function() {
  'use strict';
  
  if (window.__cgScrollWatchdogV22) return;
  window.__cgScrollWatchdogV22 = true;
  
  const LOCK_CLASSES = ['cg-popup-open', 'cg-scroll-locked', 'drawer-open', 'menu-open', 'modal-open', 'overflow-hidden'];
  const MODAL_SELECTORS = [
    '#cg-welcome-popup-overlay.is-visible',
    '.cart-drawer.is-open',
    '#cg-mobile-menu.is-open',
    '.modal.is-open',
    '[aria-modal="true"]:not([style*="display: none"])',
    'dialog[open]'
  ];
  
  function isAnyModalVisible() {
    for (const selector of MODAL_SELECTORS) {
      const el = document.querySelector(selector);
      if (el) {
        const style = window.getComputedStyle(el);
        if (style.display !== 'none' && style.visibility !== 'hidden' && parseFloat(style.opacity) > 0.1) {
          return true;
        }
      }
    }
    return false;
  }
  
  function clearAllLockClasses() {
    const html = document.documentElement;
    const body = document.body;
    let cleared = [];
    
    for (const cls of LOCK_CLASSES) {
      if (html.classList.contains(cls)) {
        html.classList.remove(cls);
        cleared.push('html.' + cls);
      }
      if (body.classList.contains(cls)) {
        body.classList.remove(cls);
        cleared.push('body.' + cls);
      }
    }
    return cleared;
  }
  
  function clearInlineOverflowStyles() {
    const html = document.documentElement;
    const body = document.body;
    let cleared = [];
    
    // Clear html inline overflow (preserve --cg-scale)
    const htmlStyle = html.getAttribute('style') || '';
    if (htmlStyle.includes('overflow')) {
      const newStyle = htmlStyle
        .replace(/overflow[^;]*;?/gi, '')
        .replace(/touch-action[^;]*;?/gi, '')
        .trim();
      html.setAttribute('style', newStyle || '');
      cleared.push('html');
    }
    
    // Clear body inline overflow completely
    const bodyStyle = body.getAttribute('style') || '';
    if (bodyStyle.includes('overflow') || bodyStyle.includes('position') || bodyStyle.includes('touch-action')) {
      const newStyle = bodyStyle
        .replace(/overflow[^;]*;?/gi, '')
        .replace(/position[^;]*;?/gi, '')
        .replace(/touch-action[^;]*;?/gi, '')
        .replace(/top[^;]*;?/gi, '')
        .trim();
      body.setAttribute('style', newStyle || '');
      cleared.push('body');
    }
    
    return cleared;
  }
  
  function diagnose() {
    const scrollContainer = document.scrollingElement || document.documentElement;
    const html = document.documentElement;
    const body = document.body;
    const htmlStyles = window.getComputedStyle(html);
    const bodyStyles = window.getComputedStyle(body);
    
    return {
      scrollingElement: scrollContainer === html ? 'html' : 'body',
      html: {
        inline: html.getAttribute('style'),
        overflow: htmlStyles.overflow,
        overflowY: htmlStyles.overflowY,
        touchAction: htmlStyles.touchAction,
        position: htmlStyles.position,
        classes: LOCK_CLASSES.filter(c => html.classList.contains(c))
      },
      body: {
        inline: body.getAttribute('style'),
        overflow: bodyStyles.overflow,
        overflowY: bodyStyles.overflowY,
        touchAction: bodyStyles.touchAction,
        position: bodyStyles.position,
        classes: LOCK_CLASSES.filter(c => body.classList.contains(c))
      },
      modalVisible: isAnyModalVisible()
    };
  }
  
  function enforceUnlock() {
    if (isAnyModalVisible()) {
      return { action: 'skip', reason: 'modal visible' };
    }
    
    const classesCleared = clearAllLockClasses();
    const stylesCleared = clearInlineOverflowStyles();
    
    if (classesCleared.length === 0 && stylesCleared.length === 0) {
      return { action: 'none', reason: 'already clean' };
    }
    
    document.body.offsetHeight; // Force repaint
    
    return {
      action: 'unlocked',
      classesCleared: classesCleared,
      stylesCleared: stylesCleared
    };
  }
  
  // MutationObserver to catch style changes from legacy scripts
  function setupMutationObserver() {
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const target = mutation.target;
          if ((target === document.body || target === document.documentElement) && !isAnyModalVisible()) {
            const style = target.getAttribute('style') || '';
            if (style.includes('overflow')) {
              // Delay to let the legacy script finish, then clean up
              setTimeout(function() {
                if (!isAnyModalVisible()) {
                  clearInlineOverflowStyles();
                }
              }, 50);
            }
          }
        }
      });
    });
    
    // Observe both html and body for style changes
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
    observer.observe(document.body, { attributes: true, attributeFilter: ['style'] });
    
    // Stop observing after 5 seconds (legacy scripts should be done by then)
    setTimeout(function() {
      observer.disconnect();
      console.log('[CG Watchdog v2.2] MutationObserver disconnected');
    }, 5000);
  }
  
  // Multi-pass cleanup to catch legacy scripts
  function runCleanupPasses() {
    // Pass 1: Immediate
    enforceUnlock();
    
    // Pass 2: After DOMContentLoaded handlers
    setTimeout(enforceUnlock, 100);
    
    // Pass 3: After most scripts
    setTimeout(enforceUnlock, 500);
    
    // Pass 4: Final safety
    setTimeout(enforceUnlock, 1500);
  }
  
  function setupEventListeners() {
    window.addEventListener('pageshow', function(e) {
      if (e.persisted) setTimeout(enforceUnlock, 100);
    });
    
    document.addEventListener('visibilitychange', function() {
      if (document.visibilityState === 'visible') setTimeout(enforceUnlock, 100);
    });
    
    window.addEventListener('orientationchange', function() {
      setTimeout(enforceUnlock, 500);
    });
    
    let resizeTimeout;
    window.addEventListener('resize', function() {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(enforceUnlock, 300);
    });
  }
  
  // Expose API
  window.cgWatchdog = {
    version: '2.2.0',
    diagnose: diagnose,
    enforceUnlock: enforceUnlock,
    clearAllLockClasses: clearAllLockClasses,
    clearInlineStyles: clearInlineOverflowStyles,
    isAnyModalVisible: isAnyModalVisible
  };
  
  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      setupMutationObserver();
      runCleanupPasses();
    });
  } else {
    setupMutationObserver();
    runCleanupPasses();
  }
  
  setupEventListeners();
  
  console.log('[CG Watchdog v2.2] Initialized - POST-LEGACY mode with MutationObserver');
})();
